import React from "react";
import * as PhosphorIcons from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Utility di formattazione €. */
export const fmtEUR = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

// Iter209dzw — Prezzo scontato fascia: intero (multiplo di 5), senza decimali.
const fmtEURInt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(Number(n) || 0);

const isDemoMode = () => localStorage.getItem("ht_demo_mode") === "1";

/**
 * Card singola servizio nella griglia marketplace.
 * Componente presentational: riceve i dati e i callback via props, nessuno stato locale.
 */
function ServiceCard({ service, user, onSelect }) {
  const s = service;
  const Icon = PhosphorIcons[s.icon] || PhosphorIcons.FileText;
  const canBuy = user.role === "professional";
  const chainedCount = (s.chained_service_ids || []).length;

  return (
    <div
      className="kpi-card group fade-up cursor-pointer"
      onClick={() => canBuy && onSelect(s)}
      data-testid={`service-${s.slug || s._id}`}
    >
      <div className="w-11 h-11 rounded-md ht-gradient border-0/10 flex items-center justify-center mb-4">
        <Icon size={22} color="#ffffff" weight="fill" />
      </div>
      <div className="label-eyebrow">{s.category}</div>
      <div className="flex items-start justify-between gap-2 mt-1">
        <h3 className="font-display text-xl font-semibold">{s.name}</h3>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 mt-0.5 rounded-full text-purple-600 hover:text-purple-800 hover:bg-purple-50 transition-colors"
              aria-label="Descrizione del servizio"
              data-testid={`service-info-${s.slug || s._id}`}
            >
              <PhosphorIcons.Info size={22} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            className="max-w-sm"
            onClick={(e) => e.stopPropagation()}
            data-testid={`service-info-content-${s.slug || s._id}`}
          >
            <div className="font-semibold text-sm mb-1">{s.name}</div>
            <p className="text-xs text-slate-600 whitespace-pre-line text-justify">
              {s.description || (s.slug ? s.slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "")}
            </p>
          </PopoverContent>
        </Popover>
      </div>
      {s.delivery_time_days > 0 && (
        <div className="flex items-center gap-1.5 mt-3 text-xs text-slate-600 text-justify" data-testid={`delivery-${s.slug}`}>
          <PhosphorIcons.Clock size={14} weight="regular" />
          <span>Consegna in giorni <strong className="text-slate-800">{s.delivery_time_days}</strong></span>
        </div>
      )}
      {chainedCount > 0 && (
        <div className="flex items-center gap-1.5 mt-1 text-xs text-purple-700 text-justify" data-testid={`chained-${s.slug}`}>
          <PhosphorIcons.LinkSimple size={14} weight="regular" />
          <span>Include {chainedCount} servizio/i prerequisito</span>
        </div>
      )}
      <div className="flex items-end justify-between mt-4 pt-4 border-t">
        <div>
          <div className="font-display text-2xl font-bold text-purple-700" data-testid={`price-${s.slug || s._id}`}>
            {isDemoMode()
              ? "€ ***"
              : (
                <>
                  {s.has_flexible_price && (
                    <span
                      className="text-sm font-medium text-purple-600/70 mr-1"
                      data-testid={`price-from-${s.slug}`}
                    >
                      da
                    </span>
                  )}
                  {s.fascia_discount_pct > 0 && Number.isInteger(Number(s.professional_price)) ? fmtEURInt(s.professional_price) : fmtEUR(s.professional_price)}
                  <span className="ml-1 text-sm font-sans font-medium text-purple-600/70" data-testid={`price-iva-${s.slug || s._id}`}>+ IVA</span>
                </>
              )
            }
          </div>
          <div className="text-xs text-muted-foreground">
            {isDemoMode()
              ? "Nascosto in demo"
              : (s.has_flexible_price ? "Prezzo base per te" : "Prezzo per te")}
          </div>
        </div>
        {canBuy && (
          <Button size="sm" className="ht-gradient border-0" data-testid={`select-${s.slug || s._id}`}>
            Prenota
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Griglia responsive di ServiceCard con search bar.
 * Props:
 *  - services: array di servizi da mostrare (già filtrati)
 *  - user: utente corrente (per abilitare Seleziona)
 *  - search / onSearchChange: stato search bar controllato dal parent
 *  - onSelect(service): callback quando si clicca una card (parent apre il PurchaseModal)
 */
export default function ServiceGrid({ services, user, search, onSearchChange, onSelect }) {
  return (
    <>
      <div className="mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="🔍 Cerca servizio per nome, descrizione o parola chiave…"
          className="w-full max-w-xl h-11 border rounded-lg px-4 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          data-testid="marketplace-search"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
        {services.map((s) => (
          <ServiceCard key={s._id} service={s} user={user} onSelect={onSelect} />
        ))}
      </div>
    </>
  );
}
